import { Check, ContentCopy, MoreHoriz, Person } from '@mui/icons-material';
import { Button, IconButton, keyframes, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { Box, styled } from '@mui/system';
import { PayloadAction } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import WmCompass from '../../assets/wm_compass_new.png';
import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import {
  getConversations,
  getFolderAgnosticConversations,
  postConvoFromMessage,
  regenerateMessage,
} from '../../redux/actions/conversationActions';
import { myFolder } from '../../redux/consts';
import { selectAppStatus } from '../../redux/reducers/appStatusReducer';
import { selectConversations, setIsLoading } from '../../redux/reducers/conversationReducer';
import { selectFolders } from '../../redux/reducers/folderReducer';
import { AppDispatch } from '../../redux/store';
import { createUserRequest } from '../../services/userRequest';
import { Conversation, Message, Metadata } from '../../Types/conversation';
import { AppEnum, DealGPTView, MessageFrom } from '../../Types/enums';
import { CustomFile } from '../../Types/file';
import { LlmEnum } from '../../Types/llm';
import { CreateUserRequest } from '../../Types/userRequest';
import ReferenceDocuments from '../DealGPT/PhaseTwo/ReferenceDocuments';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useAuth } from '../Providers/AuthProvider';
import { useChat } from '../Providers/ChatProvider';
import { useNotifications } from '../Providers/NotificationsProvider';
import ChatMCPApprovalRequest from './ChatMCPApprovalRequest';
import ChatReactMarkdown from './ChatReactMarkdown';
import ChatReasoning from './ChatReasoning';
import FileCard from './FileCard';
import FileViewDialog from './FileViewDialog';
import { RequestCreditsModal } from './RequestCreditsModal';

const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const ChatBubbleContainer = styled('div')({
  fontSize: '14px',
  letterSpacing: '0.25px',
  color: '#070154',
  'a, a:visited, a:hover, a:focus': {
    color: 'inherit',
  },
  '& .copy-icon-code': {
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },

  '&:hover .copy-icon-code': {
    opacity: 1,
  },
});

const ChatIcon = styled('img')({
  width: '20px',
});

const SpinningChatIcon = styled(ChatIcon)({
  animation: `${rotateAnimation} 2s linear infinite`,
});

const PersonIcon = styled(Person)({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
});

const ChatCard = styled('div')(({ sender }: { sender: MessageFrom }) => ({
  borderRadius: '4px',

  margin: '2px 0',
  outline: 0,
  WebkitTapHighlightColor: 'transparent',
  WebkitTextDecoration: 'none',
  textDecoration: 'none',
  color: '#000',
  boxSizing: 'border-box',
  position: 'relative',
  padding: '1rem',
  backgroundColor: sender === 'AI' ? '#fff' : '#E8EEF8',
  border: sender === 'AI' ? '1px solid rgb(244, 244, 244)' : 'none',

  '& .copy-icon': {
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },

  '&:hover .copy-icon': {
    opacity: 1,
  },
}));

const ProfilePhoto = styled('img')({
  width: '20px',
  height: '20px',
  borderRadius: '50%',
});

// Check if the metadata of the message contains Site visit notes, to display on reference documents
const getSiteNotes = (metadata: Metadata) => {
  let siteVisitNotes: string[] = [];
  //const metadata = message.metadata;
  if (!metadata) {
    return siteVisitNotes;
  }

  if (metadata.properties) {
    const notesProperty = metadata.properties.find((property) => property.name === 'SiteNotes');
    if (notesProperty) {
      // Check if SiteNotes exists and its value is an array with at least one element
      siteVisitNotes = Array.isArray(notesProperty?.value) ? notesProperty.value : [];
    }
  }

  return siteVisitNotes;
};

const ChatBubble = ({
  llm,
  message,
  privateChat,
}: {
  llm: LlmEnum;
  message: Partial<Message>;
  privateChat?: boolean;
}) => {
  const profilePhoto: string | null = localStorage.getItem('profilePhoto')
    ? localStorage.getItem('profilePhoto')
    : null;
  const navigate = useClassicNavigate();
  const analytics = useAnalytics();
  const { currentUser, refetchUser } = useAuth();
  const { refetchNotifications } = useNotifications();
  const { clearChatState } = useChat();
  const dispatch = useDispatch<AppDispatch>();
  const { copySuccess, copyToClipboard } = useCopyToClipboard();
  const { currentFolder } = useSelector(selectFolders);
  const { isLoading } = useSelector(selectConversations);
  const { selectedApp } = useSelector(selectAppStatus);
  const [anchorContextMenu, setAnchorContextMenu] = useState<null | HTMLElement>(null);
  const [viewCode, setViewCode] = useState(true);
  const [fileToView, setFileToView] = useState<CustomFile | undefined>();
  const [fileViewIndex, setFileViewIndex] = useState<number | undefined>();
  const [requestCreditsDialogOpen, setRequestCreditsDialogOpen] = useState(false);

  // Check if message contains HTML code blocks that would render as iframes
  const hasHtmlContent = useMemo(() => {
    const messageText = message.message ?? '';
    return /```html[\s\S]*?```/i.test(messageText);
  }, [message.message]);

  let siteNotes: string[] = [];
  if (message.metadata) {
    siteNotes = getSiteNotes(message.metadata);
  }
  let route = `/folder/${myFolder.id}`;
  if (currentFolder?.id) {
    route = `/folder/${currentFolder.id}`;
  }

  const handleOpenContextMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorContextMenu(event.currentTarget);
  };

  const handleCloseContextMenu = () => {
    setAnchorContextMenu(null);
  };

  const handleCopyToClipboard = useCallback(
    () => copyToClipboard(message.message ?? ''),
    [message, copyToClipboard]
  );

  const copyIcon = useMemo(() => {
    return copySuccess ? (
      <Check className="copy-icon" sx={{ color: 'green', fontSize: 18 }} />
    ) : (
      <ContentCopy className="copy-icon" sx={{ color: 'grey', fontSize: 18 }} />
    );
  }, [copySuccess]);

  const createConvoFromMessage = (message: Message) => {
    handleCloseContextMenu();
    dispatch(setIsLoading(true));
    dispatch(postConvoFromMessage(message)).then(
      (action: PayloadAction<Conversation | unknown>) => {
        const newConversation = action.payload as Conversation;
        if (selectedApp == AppEnum.DEAL_GPT) {
          analytics.event(
            AnalyticsEvent.WM_IA_CHAT_START_FROM_HERE,
            {
              id: newConversation.id,
              value: `ProjectId=${currentFolder?.projectId}`,
            },
            {
              oldMessageId: message.id,
              oldConversationId: message.conversationId,
              newConversationId: newConversation.id,
              projectId: currentFolder?.projectId,
            }
          );
        } else {
          analytics.event(
            AnalyticsEvent.WM_NIGEL_CHAT_START_FROM_HERE,
            { id: newConversation.id },
            {
              oldMessageId: message.id,
              oldConversationId: message.conversationId,
              newConversationId: newConversation.id,
            }
          );
        }
        dispatch(getConversations({ pageAction: 'reset' }));
        navigate(`${route}/${newConversation.id}`);
      }
    );
  };

  const handleMessageRegeneration = (message: Message) => {
    const messageId = message.id;

    dispatch(setIsLoading(true));
    dispatch(regenerateMessage({ message, llm })).then(
      (action: PayloadAction<Conversation | unknown>) => {
        if ((action.payload as { status?: number })?.status === 403) {
          dispatch(setIsLoading(false));
          setRequestCreditsDialogOpen(true);
          return;
        }

        const returnedConversation = action.payload as Conversation;
        // Deep Research returns original conversation, regular returns new conversation
        const isDeepResearch = returnedConversation.id === message.conversationId;

        if (selectedApp == AppEnum.DEAL_GPT) {
          analytics.event(
            AnalyticsEvent.WM_IA_CHAT_REGENERATE_RESPONSE,
            {
              id: messageId,
              value: `ProjectId=${currentFolder?.projectId},ConversationId=${returnedConversation.id}`,
            },
            {
              oldMessageId: messageId,
              newConversationId: returnedConversation.id,
              oldConversationId: message.conversationId,
              projectId: currentFolder?.projectId,
            }
          );
        } else {
          analytics.event(
            AnalyticsEvent.WM_NIGEL_REGENERATE_RESPONSE,
            { id: messageId },
            {
              oldMessageId: messageId,
              newConversationId: returnedConversation.id,
              oldConversationId: message.conversationId,
            }
          );
        }

        if (isDeepResearch) {
          analytics.event(AnalyticsEvent.WM_NIGEL_DEEP_RESEARCH_STARTED, {
            value: currentUser?.id,
          });

          enqueueSnackbar('Deep Research Started', { variant: 'info' });
          refetchUser(currentUser?.id || '');
          dispatch(setIsLoading(false));
          clearChatState();
          refetchNotifications();
          navigate(`/folder/${currentFolder?.id ?? myFolder.id}`);
        } else {
          dispatch(getFolderAgnosticConversations({ pageAction: 'reset' }));
          dispatch(getConversations({ pageAction: 'reset' }));
          navigate(`${route}/${returnedConversation.id}`);
        }
      }
    );
  };

  const handleRegenerateClick = (message: Message) => {
    handleCloseContextMenu();
    handleMessageRegeneration(message);
  };

  const handleRequestDeepResearchCredits = useCallback(async (request: CreateUserRequest) => {
    try {
      await createUserRequest(request);
      enqueueSnackbar('Request submitted successfully', { variant: 'success' });
    } catch (error) {
      if (error instanceof Error && error.message.startsWith('A pending request')) {
        enqueueSnackbar(error.message, { variant: 'warning' });
      } else {
        enqueueSnackbar('Error submitting request. Please try again.', {
          variant: 'error',
        });
      }
      return;
    }
    setRequestCreditsDialogOpen(false);
  }, []);

  return (
    <ChatCard sender={message.from ?? MessageFrom.AI} sx={{ transition: '0.5s' }}>
      <>
        <RequestCreditsModal
          open={requestCreditsDialogOpen}
          userId={currentUser?.id}
          onClose={() => setRequestCreditsDialogOpen(false)}
          onSubmit={handleRequestDeepResearchCredits}
        />
        {fileToView && fileViewIndex !== undefined && (
          <FileViewDialog
            open={true}
            file={fileToView}
            onClose={() => setFileToView(undefined)}
            llm={selectedApp === AppEnum.DEAL_GPT ? LlmEnum.OPENAI_RAG : llm}
          />
        )}
        {message.from === MessageFrom.USER ? (
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            {profilePhoto && message.userId === currentUser?.id ? (
              <ProfilePhoto src={profilePhoto} alt="Profile" />
            ) : (
              <Tooltip title={message.userPreferredName}>
                <PersonIcon />
              </Tooltip>
            )}
            <Box
              onClick={handleCopyToClipboard}
              display="flex"
              alignItems="center"
              gap={1.5}
              sx={{ cursor: 'pointer' }}
            >
              {copyIcon}
            </Box>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              {isLoading ? (
                <SpinningChatIcon src={WmCompass} alt="West Monroe Compass" />
              ) : (
                <ChatIcon src={WmCompass} alt="West Monroe Compass" />
              )}
              {message.conversationId && !privateChat && (
                <Box display="flex" alignItems="center" gap={1.5} sx={{ cursor: 'pointer' }}>
                  <div onClick={handleCopyToClipboard}>{copyIcon}</div>

                  <Tooltip
                    id={`msg-opts-label-${message.id}`}
                    PopperProps={{ keepMounted: true }}
                    title={<Typography variant={'caption'}>Message options</Typography>}
                  >
                    <IconButton
                      aria-labelledby={`msg-opts-label-${message.id}`}
                      sx={{ p: 0 }}
                      onClick={handleOpenContextMenu}
                      disabled={isLoading}
                    >
                      <MoreHoriz sx={{ width: '20px', height: '20px' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
            </div>
          </div>
        )}
        <Menu
          anchorEl={anchorContextMenu}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          keepMounted
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          open={Boolean(anchorContextMenu)}
          onClose={handleCloseContextMenu}
        >
          <MenuItem onClick={() => handleRegenerateClick(message as Message)}>
            <Typography textAlign="center">Regenerate Response</Typography>
          </MenuItem>
          <MenuItem onClick={() => createConvoFromMessage(message as Message)}>
            <Typography textAlign="center">Start New Conversation From Here</Typography>
          </MenuItem>
        </Menu>
        <ChatBubbleContainer sx={{ lineHeight: '1.5rem' }}>
          {message.from === MessageFrom.AI ? (
            // Only MessageFromAI is true
            <div>
              <div style={{ marginLeft: '20px', marginTop: '14px' }}>
                {message.reasoningSummary && <ChatReasoning message={message} />}
              </div>
              <div style={{ marginLeft: '20px', marginTop: '14px' }}>
                {message.mcpApprovalRequests &&
                  message.mcpApprovalRequests.length > 0 &&
                  message.id &&
                  message.conversationId && (
                    <ChatMCPApprovalRequest
                      messageId={message.id}
                      mcpApprovalRequests={message.mcpApprovalRequests}
                      conversationId={message.conversationId}
                    />
                  )}
              </div>
              {hasHtmlContent && message.id && (
                <Button onClick={() => setViewCode(!viewCode)}>
                  {viewCode ? 'View HTML' : 'View Code'}
                </Button>
              )}
              {<ChatReactMarkdown message={message} viewCode={viewCode} />}
              {selectedApp === AppEnum.DEAL_GPT &&
                message?.sources &&
                message.sources.length > 0 && (
                  <ReferenceDocuments
                    questionId={message.id ?? ''}
                    documents={message.sources}
                    showReferenceTitle={true}
                    siteNotes={siteNotes}
                    view={DealGPTView.CHAT}
                  />
                )}
              {isLoading && (message.message?.length ?? 0) > 0 && (
                <SpinningChatIcon src={WmCompass} alt="West Monroe Compass" />
              )}
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px', overflowX: 'auto' }}>
                {message.files && Array.isArray(message.files)
                  ? message.files.map((uploadedFile: CustomFile, index: number) => (
                      <div
                        key={index}
                        style={{
                          padding: '5px',
                          flexShrink: 0,
                          cursor: uploadedFile.deleted ? 'default' : 'pointer',
                        }}
                        onClick={
                          uploadedFile.deleted
                            ? undefined
                            : () => {
                                setFileToView(uploadedFile);
                                setFileViewIndex(index);
                              }
                        }
                      >
                        <FileCard uploadedFile={uploadedFile} llm={llm} />
                      </div>
                    ))
                  : null}
              </div>
              <p style={{ whiteSpace: 'pre-wrap' }}>{message.message}</p>
            </div>
          )}
        </ChatBubbleContainer>
      </>
    </ChatCard>
  );
};

export default ChatBubble;
